import { Formik, FormikActions, FormikProps, FormikValues, InjectedFormikProps } from 'formik';
import React from 'react';

import CurrentStep from './CurrentStep';
import WizardNavigation from './WizardNavigation';

import { WizardProps, WizardState } from './Wizard.types';

class Wizard extends React.Component<WizardProps, WizardState> {
  static Step = ({ children }: { children: any }) => children;

  static getDerivedStateFromProps(nextProps: WizardProps, prevState: WizardState): Partial<WizardState | null> {
    if (nextProps.successfulSubmit && nextProps.successPage) {
      return { showSuccessPage: nextProps.successfulSubmit };
    }

    if (nextProps.asyncSuccess !== prevState.asyncSuccess) {
      return {
        asyncSuccess: nextProps.asyncSuccess,
        waiting: false
      };
    } else return null;
  }

  constructor(props: WizardProps) {
    super(props);

    this.state = {
      step: 0,
      values: props.initialValues,
      showSuccessPage: false,
      waiting: false,
      prevValues: undefined,
      bag: undefined,
      asyncSuccess: props.asyncSuccess
    };

    this.handleReset = this.handleReset.bind(this);
  }

  componentDidUpdate(prevProps: WizardProps, prevState: WizardState) {
    const { showSuccessPage, values, bag, waiting, asyncSuccess } = this.state;

    if (prevState.showSuccessPage !== undefined && prevState.showSuccessPage !== showSuccessPage && showSuccessPage) {
      this.nextStep(values);
    }

    if (bag && asyncSuccess && !waiting) {
      this.nextStep(values);
      bag.setTouched({});
      bag.setSubmitting(false);
    }
  }

  handleReset(values: FormikValues, bag: FormikActions<FormikValues>): void {
    const { initialValues, onReset } = this.props;

    this.setState({
      step: 0,
      showSuccessPage: false,
      values: initialValues
    });

    bag.resetForm(initialValues);

    if (onReset) {
      onReset();
    }
  }

  nextStep = (values: FormikValues): void => {
    const { children } = this.props;
    const { step } = this.state;

    window.scrollTo(0, 0);

    this.setState({
      step: Math.min(step + 1, children.length - 1),
      values,
      bag: undefined
    });
  };

  previousStep = (): void => {
    const { step } = this.state;

    window.scrollTo(0, 0);

    this.setState({
      step: Math.max(step - 1, 0)
    });
  };

  handleSubmit = (values: FormikValues, bag: FormikActions<FormikValues>): void => {
    const { children, onSubmit, successPage, asyncFetching } = this.props;
    const { step } = this.state;
    const isLastStep = successPage
      ? step === React.Children.count(children) - 2
      : step === React.Children.count(children) - 1;

    if (isLastStep) {
      return onSubmit(values);
    } else {
      if (asyncFetching) {
        this.setState({
          bag: bag,
          prevValues: values,
          waiting: true
        });
      } else {
        this.nextStep(values);
        bag.setTouched({});
        bag.setSubmitting(false);
      }
    }
  };

  render(): JSX.Element {
    const {
      children,
      validation,
      isSubmitting,
      successPage,
      enableReinitialize,
      asyncFetching,
      successfulSubmit,
      buttonLabels
    } = this.props;
    const { step, values } = this.state;
    const activeStep = React.Children.toArray(children)[step];
    const stepCount = successPage ? React.Children.count(children) - 1 : React.Children.count(children);
    const isLastStep = successPage
      ? step === React.Children.count(children) - 2
      : step === React.Children.count(children) - 1;
    const isSuccessPage = successPage && step === React.Children.count(children) - 1;

    return (
      <React.Fragment>
        <CurrentStep step={step} stepCount={stepCount} isSuccessPage={isSuccessPage} />
        <Formik
          initialValues={values}
          enableReinitialize={enableReinitialize}
          validationSchema={validation['STEP_' + (step + 1)]}
          onSubmit={this.handleSubmit}
          onReset={this.handleReset}
          render={({
            handleSubmit,
            handleReset,
            isValid
          }: InjectedFormikProps<FormikProps<FormikValues>, FormikValues>): JSX.Element => (
            <form onSubmit={handleSubmit}>
              {activeStep}

              <WizardNavigation
                step={step}
                isValid={isValid}
                asyncFetching={asyncFetching}
                buttonLabels={buttonLabels}
                successPage={successPage}
                successfulSubmit={successfulSubmit}
                isLastStep={isLastStep}
                isSubmitting={isSubmitting}
                handleReset={handleReset}
                previousStep={this.previousStep}
              />
            </form>
          )}
        />
      </React.Fragment>
    );
  }
}

export { Wizard as default };
